// src/pages/QuotePage.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Card, CardContent } from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PageTitle from '../components/common/PageTitle';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { PageError } from '../components/common/PageError';
import { BeingsPageSubMenu } from '../components/menus';
import QuotesPageSubMenu from '../components/menus/QuotesPageSubMenu';
import QuoteForm from '../components/forms/QuoteForm';
import QuoteDetailCard from '../components/cards/QuoteDetailCard';
import { getCustomIcon } from '../utils/customIcons';
import { Client, clientsAPI, bookingsAPI } from '../services/api';
import { useErrorHandler, ERROR_MESSAGES } from '../errors';
import { usePermissions } from '../hooks/usePermissions';

const MainContainer = styled(Box)({ position: 'relative' });

const PageContainer = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  maxWidth: '100%',
  padding: '0',
  [theme.breakpoints.down('sm')]: { padding: '0' },
}));

const ContentCard = styled(Card)(({ theme }) => ({
  margin: '0',
  borderRadius: '0',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: 'none !important',
    borderColor: 'transparent !important',
    transform: 'none !important',
  },
  '&:last-child': { paddingBottom: 0 },
}));

type PageMode = 'new' | 'view' | 'edit';

const QuotePage: React.FC = () => {
  const params = useParams<{ id?: string; clientId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleError } = useErrorHandler();
  const { canEditBookings } = usePermissions();

  const isEditPath = location.pathname.endsWith('/edit');
  const pageMode: PageMode = !params.id ? 'new' : isEditPath ? 'edit' : 'view';

  const clientIdParam = params.clientId ? parseInt(params.clientId, 10) : null;
  const quoteIdParam = params.id ? parseInt(params.id, 10) : null;

  const [client, setClient] = useState<Client | null>(null);
  const [quote, setQuote] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (pageMode === 'new') {
        if (clientIdParam) {
          const c = await clientsAPI.getById(clientIdParam);
          setClient(c);
        } else {
          setClient(null);
        }
        setQuote(null);
        setLoading(false);
        return;
      }

      if (!quoteIdParam || isNaN(quoteIdParam) || quoteIdParam <= 0) {
        setError('Invalid quote ID');
        setLoading(false);
        return;
      }

      const loaded = await bookingsAPI.getById(quoteIdParam);

      // Guard: if a converted booking is hit via /quote/:id, bounce to /booking/:id
      if (loaded.status !== 'Quotation') {
        navigate(`/booking/${quoteIdParam}`, { replace: true });
        return;
      }

      setQuote(loaded);

      if (loaded.clientId) {
        const c = await clientsAPI.getById(loaded.clientId);
        setClient(c);
      }

      setLoading(false);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.OPERATIONS.LOAD_FAILED('quote');
      handleError(err, { context: 'loading quote', showToast: false });
      setError(message);
      setLoading(false);
    }
  }, [pageMode, clientIdParam, quoteIdParam, navigate, handleError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditClick = useCallback(() => {
    if (quoteIdParam) navigate(`/quote/${quoteIdParam}/edit`);
  }, [quoteIdParam, navigate]);

  const handleFormSuccess = useCallback(
    (bookingId: number) => {
      navigate(`/quote/${bookingId}`);
    },
    [navigate]
  );

  const handleFormCancel = useCallback(() => {
    if (pageMode === 'new' && clientIdParam) {
      navigate(`/clients/${clientIdParam}/quotations`);
    } else if (pageMode === 'edit' && quoteIdParam) {
      navigate(`/quote/${quoteIdParam}`);
    } else {
      navigate('/quotes');
    }
  }, [pageMode, clientIdParam, quoteIdParam, navigate]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner
          message={pageMode === 'new' ? 'Loading...' : 'Loading quote...'}
          size={80}
        />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageError message={error} onRetry={loadData} />
      </PageContainer>
    );
  }

  // Page title
  const pageTitle =
    pageMode === 'new'
      ? 'New Quote'
      : pageMode === 'edit'
        ? `Edit Quote ${quoteIdParam}`
        : `Quote #${quoteIdParam}`;

  const titlePrefix = client ? `${client.firstName} ${client.lastName}` : null;

  // For /new with a client context, render the BeingsPageSubMenu (Client journey)
  // so the user can navigate back to the client. For all other modes, render
  // QuotesPageSubMenu - the Prospect-Quote bubble.
  const showClientSubMenu = pageMode === 'new' && client;
  const showQuoteSubMenu = pageMode !== 'new' && quote;

  // Warning icon (if set), then the quotation type icon, matching BookingsList.
  const getHeaderLeftContent = () => (
    <>
      {client?.showWarning && (
        <Box
          sx={{
            padding: '0 0.5rem',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <WarningIcon
            sx={{
              fontSize: '2.25rem',
              color: '#fff',
              marginLeft: '0.5rem',
              '@media (min-width: 600px)': {
                marginLeft: '2rem',
              },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          paddingLeft: client?.showWarning ? 0 : '1rem',
          paddingRight: '0.5rem',
          '@media (min-width: 600px)': {
            paddingLeft: client?.showWarning ? 0 : '3rem',
          },
        }}
      >
        {getCustomIcon('quotation', '2.2rem', '#ffffff')}
      </Box>
    </>
  );

  return (
    <MainContainer>
      {titlePrefix ? (
        <PageTitle
          title={titlePrefix}
          modeText={pageTitle}
          leftContent={getHeaderLeftContent()}
        />
      ) : (
        <PageTitle title={pageTitle} leftContent={getHeaderLeftContent()} />
      )}

      {showClientSubMenu && (
        <BeingsPageSubMenu
          being={client!}
          type="client"
          showBackButton={false}
          backButtonMode="browser"
        />
      )}

      {showQuoteSubMenu && (
        <QuotesPageSubMenu
          bookingId={quote.id}
          clientId={client?.id}
          clientIsProspect={client?.isProspect ?? false}
          isConverted={quote.convertedToBookingId != null}
          showBackButton={false}
        />
      )}

      <PageContainer>
        <ContentCard>
          <CardContent sx={{ p: '0', '&:last-child': { pb: 0 } }}>
            <Box>
              {pageMode === 'view' && quote ? (
                <QuoteDetailCard
                  quote={quote}
                  clientEmail={client?.email}
                  clientPhone={client?.phone}
                  clientIsProspect={client?.isProspect ?? false}
                  onEdit={canEditBookings ? handleEditClick : undefined}
                  onRefresh={loadData}
                />
              ) : (
                <QuoteForm
                  key={quote?.id || 'new'}
                  existingClient={client}
                  existingQuote={pageMode === 'edit' ? quote : null}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              )}
            </Box>
          </CardContent>
        </ContentCard>
      </PageContainer>
    </MainContainer>
  );
};

export default QuotePage;
